import axios, { InternalAxiosRequestConfig } from 'axios';
import {
  BySource, Campaign, CampaignEngagement, CampaignSendDetail, CampaignStats, DailyEngagement,
  LeadRow, MetaCampaignSpendResponse, User,
  XReport, GoogleReport, SurfaceReport, AlbumFrame, LinkedInReport, ChatGPTReport,
  Cadence, CadenceBoard, MessageSend, SendStatsRow, WhatsAppTemplate,
} from '../types';

const API_BASE_URL = process.env.REACT_APP_CORE_API_URL || 'http://localhost:8000';
const API_KEY = process.env.REACT_APP_CORE_API_KEY || '';

export const TOUCHPOINT_URL =
  process.env.REACT_APP_TOUCHPOINT_URL || 'https://nurture.bhuvanafortunegreens.com';

// Attach the logged-in user's id so core can audit by user.
const attachUserId = (config: InternalAxiosRequestConfig) => {
  const storedUser = localStorage.getItem('campaign_portal_user');
  if (storedUser) {
    const user = JSON.parse(storedUser);
    if (user.id) {
      (config.headers as any)['X-User-Id'] = user.id;
    }
  }
  return config;
};

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${API_KEY}`,
  },
});
apiClient.interceptors.request.use(attachUserId);

// Every sign-in names this portal's surface — core refuses a user whose
// `surfaces` allowlist does not carry it (MAG-174).
const SURFACE = 'campaigns';

export const authApi = {
  verifyUser: async (email: string, name?: string, pictureUrl?: string, googleId?: string): Promise<User> => {
    const response = await apiClient.post('/api/v1/auth/verify-user', {
      email,
      name,
      picture_url: pictureUrl,
      google_id: googleId,
      surface: SURFACE,
    });
    return response.data;
  },

  loginWithPassword: async (email: string, password: string): Promise<User> => {
    const response = await apiClient.post('/api/v1/auth/login', { email, password, surface: SURFACE });
    return response.data;
  },
};

export const campaignApi = {
  listWithStats: async (): Promise<Campaign[]> => {
    const response = await apiClient.get('/api/v1/campaigns/', {
      params: { include: 'stats', limit: 100 },
    });
    return response.data.data;
  },

  get: async (campaignId: string): Promise<Campaign> => {
    const response = await apiClient.get(`/api/v1/campaigns/${campaignId}`);
    return response.data;
  },

  stats: async (campaignId: string): Promise<CampaignStats> => {
    const response = await apiClient.get(`/api/v1/campaigns/${campaignId}/stats`);
    return response.data;
  },

  // No limit param ⇒ core returns the full list.
  sends: async (campaignId: string): Promise<{ total: number; data: CampaignSendDetail[] }> => {
    const response = await apiClient.get(`/api/v1/campaigns/${campaignId}/sends`);
    return response.data;
  },

  transition: async (campaignId: string, verb: 'pause' | 'resume' | 'abort'): Promise<Campaign> => {
    const response = await apiClient.post(`/api/v1/campaigns/${campaignId}/${verb}`);
    return response.data;
  },
};

export const engagementApi = {
  forCampaign: async (campaignId: string): Promise<CampaignEngagement> => {
    const response = await apiClient.get(`/api/v1/campaigns/${campaignId}/engagement`);
    return response.data;
  },

  // IST days, YYYY-MM-DD. Omit both for today; dateTo makes it an
  // inclusive range (server owns the IST windowing). sort: recent (default)
  // | opens | frames | dwell | load | likes | shares | brochure —
  // server-side, applied before pagination.
  daily: async (date?: string, dateTo?: string, limit?: number, offset?: number, sort?: string, source?: string | null, formType?: string | null, converted?: boolean): Promise<DailyEngagement> => {
    const params: Record<string, string> = {};
    if (date) params.date = date;
    if (dateTo) params.date_to = dateTo;
    if (limit !== undefined) params.limit = String(limit);
    if (offset !== undefined) params.offset = String(offset);
    if (sort && sort !== 'recent') params.sort = sort;
    if (source) params.source = source;
    if (formType) params.form_type = formType;
    if (converted) params.converted = 'true';
    const response = await apiClient.get('/api/v1/engagement/daily', { params });
    return response.data;
  },

  // Every arrival source active in an inclusive IST-day range, with its
  // funnel. `since` is required by the server (an unbounded rollup would mix
  // pre-launch days into every rate).
  // One owned entry point over an inclusive IST-day range.
  surface: async (surface: 'website' | 'album', since: string, until: string): Promise<SurfaceReport> => {
    const response = await apiClient.get(`/api/v1/engagement/surface/${surface}`, { params: { since, until } });
    return response.data;
  },

  bySource: async (since: string, until: string, cut?: 'hour'): Promise<BySource> => {
    const response = await apiClient.get('/api/v1/engagement/by-source', { params: { since, until, cut } });
    return response.data;
  },
};

// Marketing-service: Meta campaign spend for the Dashboard's cost columns.
// Separate client — different box service, same Bearer pattern.
const marketingClient = axios.create({
  baseURL: process.env.REACT_APP_MARKETING_API_URL || 'https://marketing-service.bhuvanafortunegreens.com',
  headers: { 'Authorization': `Bearer ${process.env.REACT_APP_MARKETING_API_KEY || ''}` },
});

// Our ad account. The marketing DB also syncs the Bhuvana Group account
// (the agency's lead-form and boost campaigns); nothing here reads it.
// Without it the spend read sums every synced account — refuse to build a page
// on that rather than show a silently doubled number.
export const META_AD_ACCOUNT_ID = process.env.REACT_APP_META_AD_ACCOUNT_ID;
if (!META_AD_ACCOUNT_ID) throw new Error('REACT_APP_META_AD_ACCOUNT_ID is not set — copy .env before building');

export const marketingApi = {
  // Inclusive dates, YYYY-MM-DD (insight rows are per-day).
  campaignSpend: async (since: string, until: string): Promise<MetaCampaignSpendResponse> => {
    const response = await marketingClient.get('/api/v1/meta-ads/campaign-spend', {
      params: { since, until, account_id: META_AD_ACCOUNT_ID },
    });
    return response.data;
  },

  // Pull fresh insights for our account (default window: 3 days back → today,
  // for late attribution). Skips the creative-media sweep.
  refreshInsights: async (): Promise<void> => {
    await marketingClient.post('/api/v1/meta-ads/sync', {
      account_id: META_AD_ACCOUNT_ID, insights_only: true,
    });
  },
};

// X ads, synced from the X Ads MCP into marketing-service's own tables.
export const xAdsApi = {
  report: async (since: string, until: string): Promise<XReport> => {
    const response = await marketingClient.get('/api/v1/x-ads/report', { params: { since, until } });
    return response.data;
  },

  // Re-read the last 3 days → now (X restates billing for a while).
  refresh: async (): Promise<void> => {
    await marketingClient.post('/api/v1/x-ads/sync', {});
  },
};

// Google Ads, synced from the Google Ads API into marketing-service's own
// tables; the report arrives already joined to our arrivals by click id.
// The album catalog: frame ids → captions, read at render time so the portal
// never names a frame itself.
export const albumApi = {
  frames: async (): Promise<AlbumFrame[]> => {
    const response = await marketingClient.get('/api/v1/album/frames');
    return response.data.frames;
  },
};

export const googleAdsApi = {
  report: async (since: string, until: string): Promise<GoogleReport> => {
    const response = await marketingClient.get('/api/v1/google-ads/report', { params: { since, until } });
    return response.data;
  },
  refresh: async (): Promise<void> => {
    await marketingClient.post('/api/v1/google-ads/sync', {});
  },
};

// LinkedIn ads, synced from the Marketing API into marketing-service's own tables.
export const linkedInAdsApi = {
  report: async (since: string, until: string): Promise<LinkedInReport> => {
    const response = await marketingClient.get('/api/v1/linkedin-ads/report', { params: { since, until } });
    return response.data;
  },
  refresh: async (): Promise<void> => {
    await marketingClient.post('/api/v1/linkedin-ads/sync', {});
  },
};

// ChatGPT ads, synced from the OpenAI Ads API into marketing-service's own tables.
export const openAIAdsApi = {
  report: async (since: string, until: string): Promise<ChatGPTReport> => {
    const response = await marketingClient.get('/api/v1/openai-ads/report', { params: { since, until } });
    return response.data;
  },
  refresh: async (): Promise<void> => {
    await marketingClient.post('/api/v1/openai-ads/sync', {});
  },
};

export const leadApi = {
  // The first-touch cohort: leads whose acquisition.src is one of `tags`,
  // created in the inclusive IST-day window. Do-not-contact leads
  // included — the cohort is counted, not worked.
  byFirstTouch: async (tags: string[], since: string, until: string): Promise<LeadRow[]> => {
    if (tags.length === 0) return [];
    const response = await apiClient.get('/api/v1/leads/', {
      params: {
        acquisition_src: tags.join(','), created_since: since, created_until: until,
        include_do_not_contact: true, limit: 500, sort_by: 'created_at', sort_order: 'desc',
      },
    });
    return response.data.data;
  },

  // Exactly these leads (a platform page listing the leads its join found).
  byIds: async (ids: string[]): Promise<LeadRow[]> => {
    if (ids.length === 0) return [];
    const response = await apiClient.get('/api/v1/leads/', {
      params: { ids: ids.join(','), include_do_not_contact: true, limit: 500, sort_by: 'created_at', sort_order: 'desc' },
    });
    return response.data.data;
  },
};

// ---- the messages module (MAG-77) ----

export interface MessageSendFilter {
  template_id?: string;
  producer?: string;
  producer_ref_prefix?: string;
  campaign_id?: string;
  cadence_id?: string; // sends any step of this cadence produced
  segment?: 'replied' | 'visited' | 'failed' | 'read' | 'delivered' | 'reached';
  since?: string; // ISO instant; filters created_at >= since
  until?: string; // ISO instant; filters created_at < until
  automated_only?: boolean;
}

export const messageSendsApi = {
  // `total` is the count before paging.
  list: async (filter: MessageSendFilter, skip: number, limit: number): Promise<{ total: number; data: MessageSend[] }> => {
    const response = await apiClient.get('/api/v1/message-sends', { params: { ...filter, skip, limit } });
    return response.data;
  },
  stats: async (by: 'template' | 'producer' | 'week' | 'step',
                filter: Pick<MessageSendFilter, 'producer' | 'template_id' | 'producer_ref_prefix' | 'cadence_id' | 'since' | 'until'> = {}): Promise<SendStatsRow[]> => {
    const response = await apiClient.get('/api/v1/message-sends/stats', { params: { by, ...filter } });
    return response.data;
  },
};

export const cadenceApi = {
  list: async (): Promise<Cadence[]> => (await apiClient.get('/api/v1/cadences')).data,
  // Enrolments in attach order with a cell per step; `total` before paging.
  board: async (cadenceId: string, skip: number, limit: number): Promise<CadenceBoard> =>
    (await apiClient.get(`/api/v1/cadences/${cadenceId}/board`, { params: { skip, limit } })).data,
};

// Templates live in marketing-service (retired ones included: a send's
// template may have been retired since).
export const templateApi = {
  list: async (): Promise<WhatsAppTemplate[]> =>
    (await marketingClient.get('/api/v1/whatsapp-templates', { params: { include_retired: true } })).data.templates,
};
